import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, Button, Tag, Space, Tabs, Badge, Row, Col
} from 'antd';

function getMetaBodyPreview(t) {
  const c = t?.components?.find((x) => (x.type || '').toUpperCase() === 'BODY');
  const text = (c?.text || t?.description || '').trim();
  return text || '—';
}

function getInternalBodyPreview(t) {
  if (!t?.content) return '—';
  try {
    const arr = typeof t.content === 'string' ? JSON.parse(t.content) : t.content;
    const body = Array.isArray(arr)
      ? arr.find((x) => (x.type || '').toUpperCase() === 'BODY')
      : null;
    return (body?.text || '').trim() || '—';
  } catch {
    return '—';
  }
}

/** Meta / 範本資料庫：預覽方塊（仿 WhatsApp Manager 卡片） */
function TemplatePreviewCard({
  title,
  statusText,
  statusOk,
  categoryTag,
  languageTag,
  extraTags,
  bodyPreview,
  footerSlug,
  onSelect,
  selectLabel,
  disabled
}) {
  return (
    <div
      style={{
        border: '1px solid #e8e8e8',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ background: '#efe6dc', padding: '12px 12px 8px', flex: '0 0 auto' }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            padding: '10px 12px',
            boxShadow: '0 1px 2px rgba(0,0,0,.08)',
            fontSize: 13,
            color: '#111',
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            maxHeight: 96,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {bodyPreview}
        </div>
        <div style={{ textAlign: 'right', fontSize: 11, color: '#999', marginTop: 6 }}>16:38</div>
      </div>
      <div style={{ padding: '10px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14, wordBreak: 'break-all' }}>{title}</strong>
            {statusText && (
              <span style={{ fontSize: 12, color: statusOk ? '#52c41a' : '#888', whiteSpace: 'nowrap' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    background: statusOk ? '#52c41a' : '#d9d9d9',
                    marginRight: 4,
                    verticalAlign: 'middle'
                  }}
                />
                {statusText}
              </span>
            )}
          </div>
          <Space size={6} wrap style={{ marginTop: 6 }}>
            {categoryTag && <Tag color="blue">{categoryTag}</Tag>}
            {languageTag && <Tag color="orange">{languageTag}</Tag>}
            {extraTags}
          </Space>
          {footerSlug && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, wordBreak: 'break-all' }}>
              {footerSlug}
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <Button type="primary" size="small" disabled={disabled} onClick={(e) => { e.stopPropagation(); onSelect(); }}>
            {selectLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 模板選擇 Modal（Meta 官方 / 內部）
const TemplateModal = ({
  visible,
  onCancel,
  templates,
  metaTemplates,
  onSelectTemplate,
  t
}) => {
  const [activeTab, setActiveTab] = useState('meta');

  useEffect(() => {
    if (visible) {
      setActiveTab('meta');
    }
  }, [visible]);

  const metaCount = metaTemplates?.length || 0;
  const intCount = templates?.length || 0;

  const selectLabel = t('workflowList.select');

  const renderMetaGrid = () => (
    <div style={{ maxHeight: 480, overflow: 'auto', paddingRight: 4 }}>
      <Row gutter={[12, 12]}>
        {(metaTemplates || []).map((template) => (
          <Col xs={24} sm={12} key={template.id || template.name}>
            <TemplatePreviewCard
              title={template.name}
              statusText={
                template.status === 'APPROVED'
                  ? t('workflowDesigner.metaTemplate.approved')
                  : template.status === 'PENDING'
                    ? t('workflowDesigner.metaTemplate.pending')
                    : template.status === 'REJECTED'
                      ? t('workflowDesigner.metaTemplate.rejected')
                      : template.status
              }
              statusOk={template.status === 'APPROVED'}
              categoryTag={template.category}
              languageTag={template.language}
              bodyPreview={getMetaBodyPreview(template)}
              footerSlug={null}
              onSelect={() => {
                console.log('🎯 模板選擇:', { template: template.name, isMetaTemplate: true, templateId: template.id });
                onSelectTemplate(template, true);
              }}
              selectLabel={selectLabel}
            />
          </Col>
        ))}
      </Row>
      {(!metaTemplates || metaTemplates.length === 0) && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('workflowDesigner.noTemplatesAvailable')}
        </div>
      )}
    </div>
  );

  const renderInternalGrid = () => (
    <div style={{ maxHeight: 480, overflow: 'auto', paddingRight: 4 }}>
      <Row gutter={[12, 12]}>
        {(templates || []).map((template) => (
          <Col xs={24} sm={12} key={template.id}>
            <TemplatePreviewCard
              title={template.name}
              statusText={null}
              statusOk={false}
              categoryTag={template.category}
              languageTag={template.language}
              extraTags={template.templateType ? <Tag color="green">{template.templateType}</Tag> : null}
              bodyPreview={getInternalBodyPreview(template)}
              footerSlug={template.description || null}
              onSelect={() => {
                console.log('🎯 模板選擇:', { template: template.name, isMetaTemplate: false, templateId: template.id });
                onSelectTemplate(template, false);
              }}
              selectLabel={selectLabel}
            />
          </Col>
        ))}
      </Row>
      {(!templates || templates.length === 0) && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('workflowDesigner.noTemplatesAvailable')}
        </div>
      )}
    </div>
  );

  const items = [
    {
      key: 'meta',
      label: (
        <span>
          {t('workflowDesigner.metaTemplate.title')}
          {metaCount > 0 && (
            <Badge count={metaCount} style={{ marginLeft: 8, backgroundColor: '#1890ff' }} />
          )}
        </span>
      ),
      children: renderMetaGrid()
    },
    {
      key: 'internal',
      label: (
        <span>
          {t('workflowDesigner.internalTemplate')}
          {intCount > 0 && (
            <Badge count={intCount} style={{ marginLeft: 8, backgroundColor: '#52c41a' }} />
          )}
        </span>
      ),
      children: renderInternalGrid()
    }
  ];

  return (
    <Modal
      title={t('workflowDesigner.selectWhatsAppTemplate')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={960}
      zIndex={1100}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />
    </Modal>
  );
};

export default React.memo(TemplateModal);
